import React, { useState } from 'react';
import { 
  Container, 
  Typography, 
  Paper, 
  Box, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails,
  TextField,
  Button,
  Grid,
  Divider,
  Tab,
  Tabs
} from '@mui/material';
import { 
  ExpandMore as ExpandMoreIcon, 
  Search as SearchIcon,
  TravelExplore as TravelIcon,
  Event as EventIcon,
  People as PeopleIcon,
  Photo as PhotoIcon,
  Map as MapIcon,
  Help as HelpIcon
} from '@mui/icons-material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`help-tabpanel-${index}`}
      aria-labelledby={`help-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const HelpPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };
  
  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();

    // In a real app, you would filter FAQs or perform a search against your help content
  };

  return (
    <Container maxWidth="lg">
      <Paper elevation={3} sx={{ p: 4, my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Help Center
        </Typography>
        
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" paragraph>
            Find answers to common questions and learn how to make the most of our platform.
          </Typography>
          
          <Box component="form" onSubmit={handleSearch} sx={{ mt: 3 }}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs>
                <TextField
                  fullWidth
                  placeholder="Search for help articles..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>
              <Grid item>
                <Button 
                  type="submit" 
                  variant="contained"
                >
                  Search
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Box>
        
        <Divider sx={{ mb: 3 }} />
        
        <Box sx={{ width: '100%' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange} 
              variant="scrollable"
              scrollButtons="auto"
              aria-label="help topics tabs"
            >
              <Tab icon={<HelpIcon />} iconPosition="start" label="Getting Started" />
              <Tab icon={<TravelIcon />} iconPosition="start" label="Trips" />
              <Tab icon={<EventIcon />} iconPosition="start" label="Events" />
              <Tab icon={<PeopleIcon />} iconPosition="start" label="People & Invitations" />
              <Tab icon={<PhotoIcon />} iconPosition="start" label="Photos" />
              <Tab icon={<MapIcon />} iconPosition="start" label="Maps & Timeline" />
            </Tabs>
          </Box>
          
          <TabPanel value={tabValue} index={0}>
            <Typography variant="h6" gutterBottom>
              Getting Started with ItineraryMap
            </Typography>
            
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="medium">How do I create an account?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography paragraph>
                  To create an account, simply click on the "Sign Up" button in the top-right corner of the homepage. 
                  You can sign up using your email address or through your Google account for faster registration.
                </Typography>
                <Typography paragraph>
                  After registering, you'll be able to set up your profile and start planning your trips right away!
                </Typography>
              </AccordionDetails>
            </Accordion>
            
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="medium">What can I do with ItineraryMap?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography paragraph>
                  With ItineraryMap, you can:
                </Typography>
                <ul>
                  <li>Plan and organize trips</li>
                  <li>Create and manage events</li>
                  <li>Invite friends and family to your trips and events</li>
                  <li>Upload and organize travel photos</li>
                  <li>Visualize your trips on interactive maps</li>
                  <li>View a timeline of your travel experiences</li>
                  <li>Connect with other travelers</li>
                </ul>
              </AccordionDetails>
            </Accordion>
            
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="medium">Is ItineraryMap free to use?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography paragraph>
                  ItineraryMap offers both free and premium subscription options. With a free account, you can create trips, 
                  add photos, and use basic features.
                </Typography>
                <Typography paragraph>
                  Premium subscribers enjoy additional benefits such as unlimited photo storage, advanced trip planning tools, 
                  priority support, and more. Visit the pricing page to learn more about our subscription options.
                </Typography>
              </AccordionDetails>
            </Accordion>
          </TabPanel>
          
          <TabPanel value={tabValue} index={1}>
            <Typography variant="h6" gutterBottom>
              Managing Your Trips
            </Typography>
            
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="medium">How do I create a new trip?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography paragraph>
                  To create a new trip, navigate to the "Trips" section from the main menu and click the "Add Trip" button.
                  You'll need to provide basic information like trip title, destination, start and end dates.
                </Typography>
                <Typography paragraph>
                  Once created, you can add more details such as accommodations, transportation, activities, and photos to your trip.
                </Typography>
              </AccordionDetails>
            </Accordion>
            
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="medium">Can I share my trip with others?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography paragraph>
                  Yes! You can invite friends and family to view or collaborate on your trip. From your trip details page, 
                  click the "Share" button and enter the email addresses of the people you want to invite.
                </Typography>
                <Typography paragraph>
                  You can set different permission levels for each person: Viewer (can only see trip details) or 
                  Collaborator (can edit and contribute to the trip).
                </Typography>
              </AccordionDetails>
            </Accordion>
          </TabPanel>
          
          <TabPanel value={tabValue} index={2}>
            <Typography variant="h6" gutterBottom>
              Events Management
            </Typography>
            
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="medium">How do I create an event?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography paragraph>
                  To create a new event, go to the "Events" section and click "Create Event." Fill in details such as 
                  event name, date, time, location, and description.
                </Typography>
                <Typography paragraph>
                  You can also set privacy settings, add a cover photo, and specify if the event is part of a trip.
                </Typography>
              </AccordionDetails>
            </Accordion>
            
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="medium">Can I create recurring events?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography paragraph>
                  Yes, we support recurring events. When creating an event, check the "Make this a recurring event" option 
                  and specify your recurrence pattern (daily, weekly, monthly, etc.) and how long it should repeat.
                </Typography>
                <Typography paragraph>
                  This is useful for regular activities like weekly meetups or monthly travel club gatherings.
                </Typography>
              </AccordionDetails>
            </Accordion>
          </TabPanel>
          
          <TabPanel value={tabValue} index={3}>
            <Typography variant="h6" gutterBottom>
              People & Invitations
            </Typography>
            
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="medium">How do I invite people to my trip or event?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography paragraph>
                  From your trip or event page, click the "Invite" button. Enter email addresses of people you want to invite, 
                  add an optional personal message, and send the invitation.
                </Typography>
                <Typography paragraph>
                  Invitees will receive an email with a link to join. If they don't have an account, 
                  they'll be prompted to create one.
                </Typography>
              </AccordionDetails>
            </Accordion>
            
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="medium">How do I manage my network?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography paragraph>
                  Go to the "People" section to see all your connections. Here you can view people you've traveled with, 
                  organize your contacts, and manage your relationships.
                </Typography>
                <Typography paragraph>
                  You can categorize people as family, friends, colleagues, or create custom categories to help keep 
                  your network organized.
                </Typography>
              </AccordionDetails>
            </Accordion>
          </TabPanel>
          
          <TabPanel value={tabValue} index={4}>
            <Typography variant="h6" gutterBottom>
              Photos Management
            </Typography>
            
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="medium">How do I upload photos?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography paragraph>
                  You can upload photos from the "Photos" section or directly from a trip or event page. 
                  Click "Upload Photos" and select images from your device.
                </Typography>
                <Typography paragraph>
                  We support batch uploads, so you can select multiple photos at once. Make sure your photos are in 
                  JPG, PNG, or HEIF format and under 25MB each.
                </Typography>
              </AccordionDetails>
            </Accordion>
            
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="medium">Are my photos automatically organized?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography paragraph>
                  Yes! We use the EXIF data from your photos (if available) to automatically organize them by date and location.
                  When you upload photos to a specific trip or event, they're automatically associated with it.
                </Typography>
                <Typography paragraph>
                  You can also manually organize photos into albums or associate them with specific trips and events later.
                </Typography>
              </AccordionDetails>
            </Accordion>
          </TabPanel>
          
          <TabPanel value={tabValue} index={5}>
            <Typography variant="h6" gutterBottom>
              Maps & Timeline Features
            </Typography>
            
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="medium">How does the map feature work?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography paragraph>
                  Our interactive map displays all your trips and events geographically. You can zoom in/out, filter by date range, 
                  type (trip/event), and more.
                </Typography>
                <Typography paragraph>
                  Click on a pin to see details about that location, including any photos you've taken there, events, 
                  or trips associated with it.
                </Typography>
              </AccordionDetails>
            </Accordion>
            
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="medium">What is the timeline feature?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography paragraph>
                  The timeline provides a chronological view of all your travel experiences. It includes trips, events, 
                  and photos arranged by date, allowing you to visualize your journey over time.
                </Typography>
                <Typography paragraph>
                  You can filter the timeline by year, trip, or type of activity, and share specific portions with friends or family.
                </Typography>
              </AccordionDetails>
            </Accordion>
          </TabPanel>
        </Box>
        
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Still need help?
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            size="large"
            href="/contact"
            sx={{ mt: 1 }}
          >
            Contact Support
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default HelpPage; 